import { queryOptions } from '@tanstack/react-query'
import { apiGet } from './client'

/**
 * Tablourile si rapoartele Easeedesk mutate in v4: tabloul de tichete,
 * "Dashboards & Reports" si garda.
 *
 * Stau in fisierul lor, nu in `api/easeedesk.ts`: acolo scriu in acelasi timp
 * ramurile care muta lista de tichete si fisa de aparat, si doua benzi in
 * acelasi fisier inseamna o impacare in plus la fiecare livrare.
 */

/**
 * Filtrele cu care se deschide lista cand se apasa casuta - le alcatuieste
 * serverul (`App\Easeedesk\Boards\TileFilter`), pe randul si centrul casutei,
 * din aceleasi tipuri, stari si grupuri cu care a fost numarata.
 */
export interface BoardTileFilters {
  type: number[]
  status: number[]
  group: number[]
  /** "unassigned" pe randul lui; gol in rest. */
  advanced: string
  /** "1" | "2" | "3" - scurtaturile de fereastra ale listei; gol in rest. */
  filterFrom: string
}

/** O casuta de pe tablou: cifra, tichetul din spatele ei, si de cand sta. */
export interface BoardCell {
  count: number
  /** 0 = nu e niciun tichet, deci casuta a doua nu e legatura. */
  ticket: number
  /** "2h5m ago", gol cand nu e nimic. */
  age: string
  filters: BoardTileFilters
}

export interface BoardCentre {
  key: string
  label: string
  color: string
}

export interface BoardRow {
  key: string
  label: string
  color: string
}

export interface BoardAged {
  ticket: number
  age: string
}

export interface TicketsDashboard {
  ready: boolean
  from: string
  to: string
  centers: BoardCentre[]
  rows: BoardRow[]
  cells: Record<string, Record<string, BoardCell>>
  averages: Record<string, string>
  activity: Record<string, string | BoardAged>
  elapsed: string
}

export interface ReportLink {
  label: string
  url: string
  blank: boolean
  title: string
}

export interface AllReports {
  show: boolean
  title: string
  groups: { tone: string; links: ReportLink[] }[]
}

export interface OncallGroup {
  id: number
  name: string
}

export interface OncallGroups {
  ready: boolean
  groups: OncallGroup[]
}

export interface OncallUser {
  id: number
  user_id: number
  name: string
  date_from: string
  date_to: string
  phone: string
}

export interface OncallUsers {
  group: number
  users: OncallUser[]
}

/** Perioada tabloului; goala = ziua de azi, ca in ecranul vechi. */
export function ticketsDashboardPath(from: string, to: string): string {
  const p = new URLSearchParams()
  if (from) p.set('from', from)
  if (to) p.set('to', to)
  const q = p.toString()
  return q ? `/easeedesk/tickets-dashboard?${q}` : '/easeedesk/tickets-dashboard'
}

export function ticketsDashboardQuery(from: string, to: string) {
  return queryOptions({
    queryKey: ['easeedesk', 'tickets-dashboard', from, to],
    queryFn: () => apiGet<TicketsDashboard>(ticketsDashboardPath(from, to)),
    /* Ecranul vechi se reimprospata singur la un minut. */
    refetchInterval: 60_000,
  })
}

export const allReportsQuery = queryOptions({
  queryKey: ['easeedesk', 'all-reports'],
  queryFn: () => apiGet<AllReports>('/easeedesk/all-reports'),
})

export const oncallQuery = queryOptions({
  queryKey: ['easeedesk', 'oncall'],
  queryFn: () => apiGet<OncallGroups>('/easeedesk/oncall'),
})

export function oncallUsersQuery(group: number) {
  return queryOptions({
    queryKey: ['easeedesk', 'oncall', group],
    queryFn: () => apiGet<OncallUsers>(`/easeedesk/oncall/${group}/users`),
  })
}
